import { FC } from "react";
import Table from "react-bootstrap/Table";
import Button from "react-bootstrap/Button";
import Alert from "react-bootstrap/Alert";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import { FaEdit, FaTrash } from "react-icons/fa";
import { mapNodes } from "app/util/graph";
import { CompanyContactListFieldsFragment, useCompanyContactsQuery } from "app/api/graph/types";
import { LoadingSpinner } from "app/component/util/LoadingSpinner";
import { deleteCompanyContactRoute, editCompanyContactRoute } from "app/route/Routes";
import { companyGraphId } from "app/api/graph/helpers";
import { BlockButtonGroup } from "app/component/button/BlockButtonGroup";
import { ButtonLink } from "app/component/button/ButtonLink";
import { useGeneralPermissions } from "app/api/graph/hook/use-general-permissions";

type TableRowProps = {
    companyId: number;
    companyContact: CompanyContactListFieldsFragment;
};

const TableRow = ({ companyId, companyContact }: TableRowProps) => {
    const permissions = useGeneralPermissions(companyContact.permissions);

    return (
        <tr key={companyContact.id}>
            <td>{companyContact.name}</td>
            <td>
                {permissions.canUpdate && (
                    <ButtonGroup>
                        <ButtonLink
                            to={editCompanyContactRoute.build({
                                companyId: companyId,
                                contactId: companyContact._id,
                            })}
                            variant="warning"
                            size="sm"
                            title="Edit"
                        >
                            <FaEdit />
                        </ButtonLink>
                    </ButtonGroup>
                )}
                {permissions.canDelete && (
                    <ButtonGroup>
                        <ButtonLink
                            to={deleteCompanyContactRoute.build({
                                companyId: companyId,
                                contactId: companyContact._id,
                            })}
                            variant="danger"
                            size="sm"
                            title="Delete"
                        >
                            <FaTrash />
                        </ButtonLink>
                    </ButtonGroup>
                )}
            </td>
        </tr>
    );
};
type Props = {
    companyId: number;
};

export const CompanyContactsTable: FC<Props> = ({ companyId }) => {
    const { data, loading, error, fetchMore } = useCompanyContactsQuery({
        variables: { companyId: companyGraphId(companyId) },
        notifyOnNetworkStatusChange: true,
    });
    const companyContacts = data?.company?.contacts;

    if (loading && !companyContacts) {
        return <LoadingSpinner />;
    }
    if (error) {
        return <Alert variant="danger">{error.message}</Alert>;
    }
    if (!companyContacts) {
        return <Alert variant="danger">Not found.</Alert>;
    }

    const fetchMoreCompanyContact = () => fetchMore({ variables: { after: companyContacts.pageInfo.endCursor } });

    return (
        <Table responsive="sm" bordered className="crud-table">
            <thead>
                <tr>
                    <th>Name</th>
                    <th>Actions</th>
                </tr>
            </thead>
            <tbody>
                {mapNodes(companyContacts, (companyContact) => (
                    <TableRow key={companyContact.id} companyId={companyId} companyContact={companyContact} />
                ))}
                {companyContacts.edges?.length === 0 ? (
                    <tr>
                        <td colSpan={100}>No company contacts.</td>
                    </tr>
                ) : null}
                {loading ? (
                    <tr>
                        <td colSpan={100}>
                            <LoadingSpinner />
                        </td>
                    </tr>
                ) : null}
                {companyContacts.pageInfo.hasNextPage ? (
                    <tr>
                        <td colSpan={100}>
                            <BlockButtonGroup>
                                <Button variant="light" onClick={fetchMoreCompanyContact}>
                                    Load more
                                </Button>
                            </BlockButtonGroup>
                        </td>
                    </tr>
                ) : null}
            </tbody>
        </Table>
    );
};
